import { createBrowserRouter, RouterProvider, Outlet, useLocation } from "react-router-dom";
import React, { useEffect, useState } from "react";
import ReactDOM from "react-dom";
import { Spin } from "antd";
import { usePageBlockStore } from "../core/store/usePageBlockStore";
import ProtectedRoute from "./auth/ProtectedRoute.tsx";
import RequireProfile from "./auth/RequireProfile.tsx";
import { ADMIN, MASTER, PAID, SIMULADO, type Profile } from "./auth/accessPolicy.ts";
import PaymentSuccessView from "./views/PaymentSuccess.view.tsx";
import PagamentoPendenteView from "./views/PagamentoPendente.view.tsx";

import NotFound404 from "./views/NotFound404.view.tsx";
import AuthorizedCallback from "./views/AuthorizedCallback.view.tsx";
import AuthService from "./auth/Authorization.service.ts";
import Contact from "./views/Contact.view";
import Home from "./views/Home.view.tsx";
import LoginView from "./views/Login.view.tsx";
import RegistroView from "./views/Registro.view.tsx";
import VerificarEmailView from "./views/VerificarEmail.view.tsx";
import EsqueciSenhaView from "./views/EsqueciSenha.view.tsx";
import RedefinirSenhaView from "./views/RedefinirSenha.view.tsx";
import PlanosPrecos from "./views/PlanosPrecos.view.tsx";
import LandingView from "./views/Landing.view.tsx";
import PublicLayout from "./views/PublicLayout.view.tsx";

import UsuariosList from "./features/UsuarioList";
import PerfilView from "./views/Perfil.view.tsx";
import UsuarioView from "./views/Usuario.view";
import Questao from "./views/Questao.view";
import QuestionList from "./views/QuestionList.view.tsx";
import BulkImportQuestoes from "./views/BulkImportQuestoes.view.tsx";
import MateriaForm from "./features/MateriaForm";
import BancaForm from "./features/BancaForm";
import CadastroSimuladoForm from "./views/CadastroSimuladoForm.view.tsx";
import ListarSimulado from "./views/ListarSimulado.view.tsx";
import RealizarSimulado from "./views/RealizarSimulado.view.tsx";
import InscricaoSimulado from "./views/InscricaoSimulado.view.tsx";
import CarrosselForm from "./features/CarrosselForm.tsx";
import ResultadoSimulado from "./views/ResultadoSimulado.view.tsx";
import HistoricoSimulado from "./views/HistoricoSimulado.view.tsx";
import CertameView from "./views/Certame.view.tsx";
import CertameEditarView from "./views/CertameEditar.view.tsx";
import CertameRevisarTagsView from "./views/CertameRevisarTags.view.tsx";
import ProdutoView from "./views/Produto.view.tsx";
import Contato from "./views/Contact.view";
import PdfAnnotatorPage from "./views/PdfAnnotatorPage.view.tsx";
import FileManager from "./views/FileManager.view.tsx";
import RevisaoView from "./views/Revisao.view.tsx";
import RevisaoEstudarView from "./views/RevisaoEstudar.view.tsx";
import NovoCartaoView from "./views/admin/NovoCartao.view.tsx";
import GerenciarCartoesView from "./views/admin/GerenciarCartoes.view.tsx";
import TextosBaseView from "./views/admin/TextosBase.view.tsx";
import ModeracaoView from "./views/admin/Moderacao.view.tsx";

function RootLayout() {
  const location = useLocation();
  const blocking = usePageBlockStore((s) => s.blocking);
  const setBlocking = usePageBlockStore((s) => s.setBlocking);

  useEffect(() => {
    setBlocking(false);
  }, [location.pathname]);

  // `--c-primary` is blue product-wide; /home is the one route that keeps the
  // brand green. The scoped overrides live in src/index.css under
  // html[data-palette="home"] — this only stamps the attribute.
  //
  // It sits here, in RootLayout, because this component is mounted for every
  // route (it is the router's root element) and therefore cannot miss a
  // navigation. DefaultLayout would look like the natural home for it, but
  // the public/landing routes don't use it, so leaving /home would not clear
  // the attribute. Absence of the attribute is the blue default, so nothing
  // needs to set it for the other routes.
  useEffect(() => {
    const isHome = location.pathname === "/home";
    if (isHome) {
      document.documentElement.setAttribute("data-palette", "home");
    } else {
      document.documentElement.removeAttribute("data-palette");
    }
  }, [location.pathname]);

  return (
    <>
      <Outlet />
      {blocking && ReactDOM.createPortal(
        <div className="fixed inset-0 z-[9999] flex flex-col items-center justify-center bg-black/50 backdrop-blur-sm">
          <Spin size="large" />
          <p className="mt-4 text-white text-base font-medium tracking-wide">Buscando questões...</p>
        </div>,
        document.body
      )}
    </>
  );
}

/**
 * Este arquivo é a política de acesso do produto.
 *
 * As listas de perfil vêm de `auth/accessPolicy.ts` e são a transcrição das
 * condições que o `Sidebar.tsx` já usava para esconder cada entrada de menu.
 * Antes elas existiam só lá: a rota ficava aberta, e quem digitasse a URL
 * montava a tela administrativa inteira até a primeira chamada voltar 403.
 *
 * Ao mexer aqui, confira o menu correspondente em `layouts/default/Sidebar.tsx`
 * — os dois precisam concordar.
 */

/** Exige apenas estar logado. */
const logged = (element: JSX.Element) => <ProtectedRoute>{element}</ProtectedRoute>;

/** Exige estar logado E ter um dos perfis. */
const gated = (allow: Profile[], element: JSX.Element) => (
  <ProtectedRoute>
    <RequireProfile allow={allow}>{element}</RequireProfile>
  </ProtectedRoute>
);

const router = createBrowserRouter([
  {
    element: <RootLayout />,
    children: [
      {
        path: "/",
        element: <PublicLayout />,
        errorElement: <NotFound404 />,
        children: [
          { index: true, element: <LandingView /> },
          { path: "login", element: <LoginView /> },
          { path: "registro", element: <RegistroView /> },
          { path: "verificar-email", element: <VerificarEmailView /> },
          { path: "esqueci-senha", element: <EsqueciSenhaView /> },
          { path: "redefinir-senha", element: <RedefinirSenhaView /> },
          { path: "planos", element: <PlanosPrecos /> },
          { path: "contato", element: <Contact /> },
        ],
      },

      // Callback do OAuth — precisa rodar antes de existir sessão.
      {
        path: "authorized",
        element: <AuthorizedCallback />,
        errorElement: <NotFound404 />,
      },

      // ── Aluno: logado, e PAID onde exige assinatura ativa ─────────────────
      { path: "home", element: gated(PAID, <Home />), errorElement: <NotFound404 /> },
      { path: "carrossel", element: logged(<CarrosselForm />), errorElement: <NotFound404 /> },
      { path: "revisao", element: gated(PAID, <RevisaoView />), errorElement: <NotFound404 /> },
      { path: "revisao/estudar", element: gated(PAID, <RevisaoEstudarView />), errorElement: <NotFound404 /> },
      { path: "perfil", element: logged(<PerfilView />), errorElement: <NotFound404 /> },
      { path: "success", element: logged(<PaymentSuccessView />), errorElement: <NotFound404 /> },
      { path: "pendente", element: logged(<PagamentoPendenteView />), errorElement: <NotFound404 /> },

      // Telas de simulado em andamento — fora do menu, alcançadas por fluxo.
      { path: "simulado-iniciado", element: logged(<RealizarSimulado />), errorElement: <NotFound404 /> },
      { path: "resultado-simulado/:registeredID", element: logged(<ResultadoSimulado />), errorElement: <NotFound404 /> },

      // ── Simulado: submenu "Simulado" (Sidebar.tsx:120-126) ────────────────
      // `competidor` fica de fora espelhando o menu — ver accessPolicy.SIMULADO.
      { path: "agenda/:tipo", element: gated(SIMULADO, <InscricaoSimulado />), errorElement: <NotFound404 /> },
      { path: "inscricao/:tipo", element: gated(SIMULADO, <InscricaoSimulado />), errorElement: <NotFound404 /> },
      { path: "historico-simulado", element: gated(SIMULADO, <HistoricoSimulado />), errorElement: <NotFound404 /> },

      // ── Administração: bloco admin do Sidebar (Sidebar.tsx:200-201) ───────
      // Simulado › Novo / Cadastrados (Sidebar.tsx:159)
      { path: "cadastro-simulado", element: gated(ADMIN, <CadastroSimuladoForm />), errorElement: <NotFound404 /> },
      { path: "editar-simulado", element: gated(ADMIN, <CadastroSimuladoForm />), errorElement: <NotFound404 /> },
      { path: "editar-simulado/:id", element: gated(ADMIN, <CadastroSimuladoForm />), errorElement: <NotFound404 /> },
      { path: "listar-simulado", element: gated(ADMIN, <ListarSimulado />), errorElement: <NotFound404 /> },

      // Cadastro
      { path: "questao", element: gated(ADMIN, <Questao />), errorElement: <NotFound404 /> },
      { path: "questoes", element: gated(ADMIN, <QuestionList />), errorElement: <NotFound404 /> },
      { path: "bulk-import", element: gated(ADMIN, <BulkImportQuestoes />), errorElement: <NotFound404 /> },
      { path: "admin/textos", element: gated(ADMIN, <TextosBaseView />), errorElement: <NotFound404 /> },
      { path: "materia", element: gated(ADMIN, <MateriaForm />), errorElement: <NotFound404 /> },
      { path: "banca", element: gated(ADMIN, <BancaForm />), errorElement: <NotFound404 /> },
      { path: "certame", element: gated(ADMIN, <CertameView />), errorElement: <NotFound404 /> },
      { path: "certame/novo", element: gated(ADMIN, <CertameEditarView />), errorElement: <NotFound404 /> },
      { path: "certame/:id", element: gated(ADMIN, <CertameEditarView />), errorElement: <NotFound404 /> },
      { path: "certame/:id/tags", element: gated(ADMIN, <CertameRevisarTagsView />), errorElement: <NotFound404 /> },

      // Moderação do fórum — abas Fórum/Denúncias (Sidebar.tsx: "Moderação")
      { path: "admin/moderacao", element: gated(ADMIN, <ModeracaoView />), errorElement: <NotFound404 /> },

      // PDFs
      { path: "admin/files", element: gated(ADMIN, <FileManager />), errorElement: <NotFound404 /> },
      { path: "admin/pdf-annotator", element: gated(ADMIN, <PdfAnnotatorPage />), errorElement: <NotFound404 /> },

      // Moderação do fórum e denúncias (abas Fórum/Denúncias)
      { path: "admin/moderacao", element: gated(ADMIN, <ModeracaoView />), errorElement: <NotFound404 /> },

      // Cartões
      { path: "admin/cartoes", element: gated(ADMIN, <GerenciarCartoesView />), errorElement: <NotFound404 /> },
      { path: "admin/cartoes/novo", element: gated(ADMIN, <NovoCartaoView />), errorElement: <NotFound404 /> },
      { path: "admin/cartoes/:id/editar", element: gated(ADMIN, <NovoCartaoView />), errorElement: <NotFound404 /> },

      // ── Master: Usuários (Sidebar.tsx:203) e Produto (Sidebar.tsx:256) ────
      // ⚠️ O Sidebar tem DUAS entradas apontando para /usuario: "Usuários ›
      // Cadastro" (master) e "Simulado › Ajuda" (adm/master/basico, :188-194).
      // O rótulo "Ajuda" é bug antigo — um `basico` que clicar nele agora cai
      // no AccessDenied. Corrigir o menu é tarefa à parte.
      { path: "usuario", element: gated(MASTER, <UsuarioView />), errorElement: <NotFound404 /> },
      { path: "usuarios", element: gated(MASTER, <UsuariosList />), errorElement: <NotFound404 /> },
      { path: "produto", element: gated(MASTER, <ProdutoView />), errorElement: <NotFound404 /> },

      { path: "*", element: <NotFound404 />, errorElement: <NotFound404 /> },
    ],
  },
]);

export default function MyRoutes() {
  return <RouterProvider router={router} />;
}
